<script setup>
import {
  ref,
  defineEmits,
  defineProps,
  onMounted,
  computed,
  nextTick,
} from "vue";
import ArgonRadio from "@/components/ArgonRadio.vue";
import { QuillEditor } from "@vueup/vue-quill";
import { QuizzesClient } from "@/services/QuizzesClient";
import { QuestionsClient } from "@/services/QuestionsClient";
import { OptionsClient } from "@/services/OptionsClient";
import Swal from "sweetalert2";

const quizzesClient = new QuizzesClient();
const questionsClient = new QuestionsClient();
const optionsClient = new OptionsClient();

const props = defineProps({
  chapterId: {
    type: Number,
    required: true,
  },
  quizzId: {
    type: Number,
    default: null,
  },
  existingQuiz: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(["delete-quiz", "save-quiz"]);

// Quiz data and loading states
const quizData = ref({ 
  capitulo_id: props.chapterId,
  id: props.quizzId,
  titulo: "",
  instrucciones: "",
  requiere_nota_minima: false, // single flag
  nota_minima: 80,
  orden: 0,
  preguntas: [],
});




const preguntas = ref([]);
const preguntaActual = ref(0);
const isLoading = ref(false);
const isLoadingQuestions = ref(false);
const isLoadingOptions = ref(false);
const isSavingQuestion = ref(false);
const isSavingOption = ref(false);
const isDeletingOption = ref(false);
const isDeletingQuestion = ref(false);

const currentQuestion = computed(() => {
  return preguntas.value[preguntaActual.value] || { texto: "", opciones: [] };
});

// Initialize with existing quiz data
onMounted(async () => {
  if (props.existingQuiz) {
    quizData.value = {
      ...props.existingQuiz,
      instrucciones: props.existingQuiz.instrucciones || "",
    };
    await fetchQuestions();
  } else {
    quizData.value.capitulo_id = props.chapterId;
    nextTick(() => {
      addQuestion();
    });
  }
});

const fetchQuestions = async () => {
  try {
    isLoadingQuestions.value = true;
    if (props.quizzId) {
      let questions = [];
      try {
        questions = await questionsClient.getQuestionsByQuizId(props.quizzId);
      } catch (err) {
        // If it's a 404, just means no questions exist yet
        if (err.message.includes("404")) {
          questions = [];
        } else {
          throw err;
        }
      }

      isLoadingOptions.value = true;
      const questionsWithOptions = await Promise.all(
        questions.map(async (question) => {
          const options = await optionsClient.getOptionsByQuestionId(question.id);
          return {
            id: question.id,
            texto: question.texto_pregunta,
            opciones: options.map((opt) => ({
              id: opt.id,
              texto: opt.texto_opcion,
              es_correcta: opt.es_correcta,
            })),
            opcionCorrectaId: options.find((opt) => opt.es_correcta)?.id || null,
          };
        })
      );

      preguntas.value = questionsWithOptions;

      // If quiz is empty, automatically add the first question
      if (preguntas.value.length === 0) {
        await addQuestion();
      }
    }
  } catch (error) {
    console.error("Error fetching questions:", error);
    Swal.fire("Atención", "El quiz aún no tiene preguntas", "info");
  } finally {
    isLoadingQuestions.value = false;
    isLoadingOptions.value = false;
  }
};


const agregarOpcion = async () => {
  try {
    isSavingOption.value = true;
    const currentQuestion = preguntas.value[preguntaActual.value];
    if (currentQuestion.opciones.length < 4) {
      if (currentQuestion.id) {
        const newOption = await optionsClient.createOption({
          pregunta_id: currentQuestion.id,
          texto_opcion: "Nueva Opción",
          es_correcta: 0,
        });

        currentQuestion.opciones.push({
          id: newOption.id,
          texto: newOption.texto_opcion,
          es_correcta: newOption.es_correcta,
        });
      } else {
        currentQuestion.opciones.push({
          id: Date.now(),
          texto: "Nueva opción",
          es_correcta: 0,
        });
      }
    }
  } catch (error) {
    console.error("Error adding option:", error);
    Swal.fire("Error", "No se pudo agregar la opción", "error");
  } finally {
    isSavingOption.value = false;
  }
};

const eliminarOpcion = async (index) => {
  try {
    isDeletingOption.value = true;
    const currentQuestion = preguntas.value[preguntaActual.value];
    if (currentQuestion.opciones.length > 1) {
      const optionId = currentQuestion.opciones[index].id;

      if (typeof optionId === "number") {
        await optionsClient.deleteOption(optionId).catch((e) => {
          if (!e.message.includes("JSON")) throw e;
        });
      }

      currentQuestion.opciones.splice(index, 1);

      if (currentQuestion.opcionCorrectaId === optionId) {
        currentQuestion.opcionCorrectaId = null;
      }
    }
  } catch (error) {
    console.error("Error deleting option:", error);
    Swal.fire("Error", "No se pudo eliminar la opción", "error");
  } finally {
    isDeletingOption.value = false;
  }
};

const toggleRadio = (selectedId) => {
  const currentQuestion = preguntas.value[preguntaActual.value];
  currentQuestion.opcionCorrectaId =
    currentQuestion.opcionCorrectaId === selectedId ? null : selectedId;

  currentQuestion.opciones.forEach((opt) => {
    opt.es_correcta = opt.id === currentQuestion.opcionCorrectaId ? 1 : 0;
  });
};

const saveQuiz = async () => {
  try {
    isLoading.value = true;

    if (!props.quizzId) {
      const newQuiz = await quizzesClient.createQuiz({
        titulo: quizData.value.titulo,
        orden: quizData.value.orden,
        instrucciones: quizData.value.instrucciones,
        capitulo_id: props.chapterId,
        requiere_nota_minima: quizData.value.requiere_nota_minima,
        nota_minima: quizData.value.nota_minima,
      });
      quizData.value.id = newQuiz.id;
    } else {
      const test = await quizzesClient.updateQuiz(props.quizzId, {
        titulo: quizData.value.titulo,
        orden: quizData.value.orden,
        instrucciones: quizData.value.instrucciones,
        requiere_nota_minima: quizData.value.requiere_nota_minima,
        bloquea_posteriores: quizData.value.requiere_nota_minima,
        nota_minima: quizData.value.nota_minima,
      });
      console.log(test);
      
    }

    for (const [index, pregunta] of preguntas.value.entries()) {
      if (pregunta.id) {
        await questionsClient.updateQuestion(pregunta.id, {
          texto_pregunta: pregunta.texto,
          quiz_id: quizData.value.id,
          orden: index + 1,
        });
      } else {
        const newQuestion = await questionsClient.createQuestion({
          quiz_id: quizData.value.id,
          texto_pregunta: pregunta.texto,
          orden: index + 1,
        });
        pregunta.id = newQuestion.id;
      }

      for (const opcion of pregunta.opciones) {
        if (opcion.id) {
          if (typeof opcion.id === "number" && opcion.id > 10000000000) {
            const newOption = await optionsClient.createOption({
              pregunta_id: pregunta.id,
              texto_opcion: opcion.texto,
              es_correcta: opcion.id === pregunta.opcionCorrectaId ? 1 : 0,
            });
            opcion.id = newOption.id;
          } else {
            await optionsClient.updateOption(opcion.id, {
              texto_opcion: opcion.texto,
              es_correcta: opcion.id === pregunta.opcionCorrectaId ? 1 : 0,
            });
          }
        }
      }
    }

    emit("save-quiz", quizData.value.id, quizData.value);
    Swal.fire("¡Guardado!", "El quiz ha sido actualizado", "success");
  } catch (error) {
    console.error("Error saving quiz:", error);
    Swal.fire("Error", "No se pudo guardar el quiz", "error");
  } finally {
    isLoading.value = false;
  }
};

const deleteQuiz = async () => {
  const result = await Swal.fire({
    title: "¿Eliminar permanentemente?",
    text: "Todos los datos asociados serán eliminados.",
    icon: "warning",
    showCancelButton: true,
    confirmButtonColor: "#dc3545", // Red
    cancelButtonColor: "#6c757d", // Gray
    confirmButtonText: "Eliminar",
    cancelButtonText: "Cancelar",
    buttonsStyling: false,
    customClass: {
      actions: "grid grid-cols-2 gap-2 w-full", // Key change for 2 columns
      confirmButton: "btn btn-danger w-full",
      cancelButton: "btn btn-secondary w-full",
    },
    reverseButtons: false, // Keep original order
  });

  if (result.isConfirmed) {
    emit("delete-quiz", {
      chapterId: props.chapterId,
      quizId: props.quizzId,
    });
  }
};

const addQuestion = async () => {
  try {
    isSavingQuestion.value = true;
    if (props.quizzId) {
      const newQuestion = {
        quiz_id: props.quizzId,
        texto_pregunta: "Nueva pregunta",
        orden: preguntas.value.length + 1,
      };

      const createdQuestion = await questionsClient.createQuestion(newQuestion);

      // Create first option (marked as correct)
      const firstOption = await optionsClient.createOption({
        pregunta_id: createdQuestion.id,
        texto_opcion: "Opción",
        es_correcta: 1,
      });

      // Create second option
      const secondOption = await optionsClient.createOption({
        pregunta_id: createdQuestion.id,
        texto_opcion: "Opción",
        es_correcta: 0,
      });

      preguntas.value.push({
        id: createdQuestion.id,
        texto: createdQuestion.texto_pregunta,
        opciones: [
          {
            id: firstOption.id,
            texto: firstOption.texto_opcion,
            es_correcta: 1,
          },
          {
            id: secondOption.id,
            texto: secondOption.texto_opcion,
            es_correcta: 0,
          },
        ],
        opcionCorrectaId: firstOption.id, // Set first option as correct
      });
    } else {
      const tempId = Date.now();
      preguntas.value.push({
        id: tempId,
        texto: "Nueva pregunta",
        opciones: [
          {
            id: tempId + 1,
            texto: "Opción correcta",
            es_correcta: 1,
          },
          {
            id: tempId + 2,
            texto: "Otra opción",
            es_correcta: 0,
          },
        ],
        opcionCorrectaId: tempId + 1, // Set first option as correct
      });
    }

    preguntaActual.value = preguntas.value.length - 1;
  } catch (error) {
    console.error("Error creating question:", error);
    Swal.fire("Error", "No se pudo crear la pregunta", "error");
  } finally {
    isSavingQuestion.value = false;
  }
};

const eliminarPregunta = async () => {
  if (preguntas.value.length > 1) {
    try {
      isDeletingQuestion.value = true;
      const questionId = preguntas.value[preguntaActual.value].id;

      if (props.quizzId && typeof questionId === "number") {
        await questionsClient.deleteQuestion(questionId).catch((e) => {
          if (!e.message.includes("JSON")) throw e;
        });
      }

      preguntas.value.splice(preguntaActual.value, 1);
      preguntaActual.value = Math.max(0, preguntaActual.value - 1);
    } catch (error) {
      console.error("Error deleting question:", error);
      Swal.fire("Error", "No se pudo eliminar la pregunta", "error");
    } finally {
      isDeletingQuestion.value = false;
    }
  }
};
</script>

<template>
  <div class="card">
    <div class="card-header d-flex pb-2 flex-column flex-md-row">
      <div class="col-12 col-md-8">
        <h6 class="font-weight-bolder mb-0">
          <i class="fas fa-question-circle pe-2"></i>
          {{ quizData.titulo || "Quiz" }}
        </h6>
        <p class="text-sm mb-1">
          Añade las preguntas que formarán parte de tu evaluación.
        </p>
      </div>

      <div
        class="col-12 col-md-4 gap-2 d-flex align-items-center justify-content-between"
      >
        <button
          class="mt-2 mb-0 ms-2 btn btn-outline-dark w-70 mt-lg-0"
          @click="saveQuiz"
          :disabled="isLoading"
        >
          <span
            v-if="isLoading"
            class="spinner-border spinner-border-sm me-1"
          ></span>
          <i v-else class="far fa-save pe-2"></i>
          Guardar
        </button>
        <button
          type="button"
          class="btn-sm mt-2 ms-0 px-4 mb-0 btn btn-dark mt-lg-0"
          @click="deleteQuiz"
          :disabled="!quizzId"
        >
          <i class="fas fa-trash"></i>
        </button>
      </div>
    </div>

    <div class="row card-body pt-1">
      <div class="col-12">
        <div class="d-flex justify-content-between align-items-center">
          <label>Título <span class="text-danger">*</span></label>
          <small class="text-muted ms-4">(máx. 100 caracteres)</small>
        </div>

        <input
          class="form-control"
          maxlength="100"
          type="text"
          v-model="quizData.titulo"
          placeholder="Titulo"
        />

        <label class="mt-3">Instrucciones</label>
        <div id="edit-quiz-instructions">
          <QuillEditor
            theme="snow"
            v-model:content="quizData.instrucciones"
            content-type="html"
            :options="{
              modules: {
                toolbar: [
                  ['bold', 'italic', 'underline'],
                  [{ list: 'ordered' }, { list: 'bullet' }],
                ],
              },
            }"
          />
        </div>

        <div class="row mt-3">
          <div class="col-md-6">
            <label class="form-label"
              >Requisito de Aprobación <span class="text-danger">*</span></label
            >
            <div class="btn-group w-100" role="group" aria-label="Requisito para continuar">
              <input
                type="radio"
                class="btn-check "
                id="completeMode"
                :value=0
                v-model="quizData.requiere_nota_minima"
              />
              <label class="btn btn-outline-dark mb-0 mb-md-3" for="completeMode">Solo completar</label>

              <input
                type="radio"
                class="btn-check"
                id="approveMode"
                :value=1
                v-model="quizData.requiere_nota_minima"
              />
              <label class="btn btn-outline-dark mb-0 mb-md-3" for="approveMode">Nota mínima</label>
            </div>


          </div>

          <div class="col-md-6" v-if="quizData.requiere_nota_minima">
            <div class="p-3 mt-0 mt-md-3">
              <div class="d-flex align-items-start">
                <div class="col-md-8 ">
                  <label for="notaMinima" class="form-label m-0 pt-2 "
                    >Nota mínima aprobatoria (%)</label
                  >
                </div>
                <div class="col-md-4">
                  <input
                    id="notaMinima"
                    type="number"
                    class="form-control form-control-sm"
                    v-model="quizData.nota_minima"
                    min="0"
                    max="100"
                    placeholder="Ej. 80"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div
          class="alert alert-light mt-3 z-index"
          :class="{ 'alert-info': quizData.requiere_nota_minima }"
        >
          <i
            class="fas"
            :class="
              quizData.requiere_nota_minima
                ? 'fa-lock text-info'
                : 'fa-unlock-alt text-muted'
            "
          ></i>
          <span class="ms-2">
            <template v-if="quizData.requiere_nota_minima">
              El contenido posterior permanecerá bloqueado hasta que el
              estudiante apruebe este quiz con al menos la
              <strong> Nota minima</strong>.
            </template>
            <template v-else>
              Los estudiantes pueden acceder al contenido posterior después de
              completar el quiz, sin necesidad de aprobar con una Nota minima.
            </template>
          </span>
        </div>
      </div>

      <hr class="horizontal dark mt-3" />

      <div>
        <ul class="nav nav-tabs text-xs px-1 mt-1">
          <li
            v-for="(item, index) in preguntas"
            :key="item.id"
            class="nav-item"
          >
            <button
              class="nav-link"
              :class="{ active: index === preguntaActual }"
              @click="preguntaActual = index"
            >
              <template
                v-if="isSavingQuestion && index === preguntas.length - 1"
              >
                <i class="fas fa-spinner fa-spin"></i>
              </template>
              <template v-else>
                {{ index + 1 }}
              </template>
            </button>
          </li>

          <li class="nav-item ms-2">
            <button
              type="button"
              @click="addQuestion"
              :disabled="isSavingQuestion"
              class="d-flex justify-content-center w-100 mt-0 mb-0 btn btn-outline-dark me-lg-0 mt-md-0"
            >
              <i
                v-if="isSavingQuestion"
                class="fas fa-spinner fa-spin d-flex align-items-center"
              ></i>
              <i
                v-else
                class="fas fa-plus-circle d-flex align-items-center"
              ></i>
            </button>
          </li>
        </ul>

        <div v-if="isLoadingQuestions" class="text-center py-4">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Cargando...</span>
          </div>
          <p class="text-muted mt-2">Cargando preguntas...</p>
        </div>

        <div v-else class="card-body px-0 py-3">
          <div v-if="isLoadingOptions" class="text-center py-4">
            <div class="spinner-border text-primary" role="status">
              <span class="visually-hidden">Cargando...</span>
            </div>
            <p class="text-muted mt-2">Cargando opciones...</p>
          </div>

          <div v-else>
            <div class="mb-3">
              <div class="d-flex justify-content-between">
                <label :for="`pregunta-${preguntaActual}`" class="form-label"
                  >Pregunta <span class="text-danger">*</span></label
                >
                <button
                  v-if="preguntas.length > 1"
                  class="btn-close ps-3 pb-2"
                  @click="eliminarPregunta"
                  :disabled="isDeletingQuestion"
                >
                  <i
                    v-if="isDeletingQuestion"
                    class="fas fa-spinner fa-spin fa-lg text-danger"
                  ></i>
                  <i v-else class="fas fa-times fa-lg text-danger"></i>
                </button>
              </div>

              <div
                :id="`pregunta-editor-${preguntaActual}`"
                class="question-editor mt-2"
              >
                <QuillEditor
                  theme="snow"
                  v-model:content="currentQuestion.texto"
                  content-type="html"
                  placeholder="Ingrese su pregunta aquí..."
                  :options="{
                    modules: {
                      toolbar: [
                        ['bold', 'italic', 'underline'],
                        [{ list: 'ordered' }, { list: 'bullet' }],
                      ],
                    },
                  }"
                />
              </div>
            </div>

            <div class="mb-3">
              <div class="d-flex justify-content-between align-items-center">
                <label class="form-label"
                  >Opciones de Respuesta
                  <span class="text-danger">*</span></label
                >
                <small class="text-muted ms-4 text-md-md text-xxs">(máx. 100 caracteres)</small>
              </div>

              <div
                v-for="(opcion, index) in preguntas[preguntaActual]?.opciones ||
                []"
                :key="opcion.id"
                class="d-flex align-items-center rounded px-2 py-0 mb-2"
              >
                <argon-radio
                  :value="opcion.id"
                  :checked="
                    preguntas[preguntaActual]?.opcionCorrectaId === opcion.id
                  "
                  @change="() => toggleRadio(opcion.id)"
                />
                <input
                  v-model="opcion.texto"
                  class="form-control me-2 py-1"
                  maxlength="100"
                  placeholder="Ingrese una opción de respuesta"
                />
                <button
                  v-if="preguntas[preguntaActual]?.opciones?.length > 2"
                  class="btn-close pb-2"
                  @click="eliminarOpcion(index)"
                  :disabled="isDeletingOption"
                >
                  <i
                    v-if="isDeletingOption"
                    class="fas fa-spinner fa-spin text-danger"
                  ></i>
                  <i v-else class="fas fa-times text-danger"></i>
                </button>
              </div>
            </div>

            <button
              v-if="preguntas[preguntaActual]?.opciones?.length < 4"
              class="w-100 mt-3 d-flex justify-content-center btn btn-outline-dark btn-sm"
              @click="agregarOpcion"
              :disabled="isSavingOption"
            >
              <i class="fas fa-plus-circle pe-2 d-flex align-items-center"></i>
              Agregar Opción
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.card {
  border-radius: 8px;
  box-shadow: 0px 2px 5px rgba(0, 0, 0, 0.1);
}

.question-editor {
  margin-bottom: 1rem;
}

.spinner-border {
  width: 1rem;
  height: 1rem;
  border-width: 0.2em;
}

.btn:disabled {
  opacity: 0.7;
}

.nav-link {
  min-width: 40px;
  text-align: center;
  color: black !important;
}

.fa-spinner {
  margin: 0 auto;
}

.nav-link.active {
  background-color: #e9ecef !important;
  color: black !important;
}

.z-index{
  z-index: 900 !important;
}

</style>